import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Bell, CircleAlert, Pencil } from 'lucide-react';
import { Card, CardBody, Button, Input, Textarea, TagChip } from '../components/ui';
import { DiscussionPoll } from '../components/DiscussionPoll';
import { AttachmentList, AttachmentPicker, AttachmentPickerLayout } from '../components/Attachments';
import { LinkifiedText } from '../components/LinkifiedText';
import { DiscussionReplyLikeControl, DiscussionTopicLikeControl } from '../components/ReplyLikeControl';
import { useReplyEmailNotificationPrompt } from '../components/ReplyEmailNotificationPrompt';
import {
  addReply,
  getTopic,
  listTags,
  setTopicReplyEmailNotifications,
  subscribeDiscussionNotificationSettings,
  subscribeReplyAttachments,
  subscribeTopicAttachments,
  subscribeReplies,
  updateTopicTagsWithSystemReply
} from '../lib/firestore';
import { editDiscussionTopic, reportDiscussionContent } from '../lib/cloudFunctions';
import { deleteDiscussionAttachment, uploadDiscussionAttachments, validateAttachmentFiles } from '../lib/attachments';
import { trackEvent } from '../lib/analytics';
import { formatExactDateTime, formatRelativeDateTime } from '../lib/dates';
import {
  groupDiscussionTags,
  normalizeDiscussionCategoryId,
  resolveDiscussionTopicTags
} from '../lib/discussionCategories';
import { memberLabel, memberPublicProfileUrl, useMemberDirectory } from '../lib/memberDirectory';
import { formatPersonName } from '../lib/names';
import { DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS, topicOverrideMode } from '../lib/notifications';
import type { DiscussionAttachment, DiscussionReply, DiscussionTopic, Tag } from '../lib/types';
import { useAuth } from '../auth/AuthProvider';

function groupThread(replies: DiscussionReply[]) {
  const top: DiscussionReply[] = [];
  const children: Record<string, DiscussionReply[]> = {};
  for (const r of replies) {
    if (!r.parentReplyId) top.push(r);
    else {
      children[r.parentReplyId] = children[r.parentReplyId] ?? [];
      children[r.parentReplyId].push(r);
    }
  }
  return { top, children };
}

function ReplyAttachments(props: { topicId: string; replyId: string }) {
  const [attachments, setAttachments] = React.useState<DiscussionAttachment[]>([]);

  React.useEffect(() => {
    return subscribeReplyAttachments(props.topicId, props.replyId, setAttachments);
  }, [props.topicId, props.replyId]);

  return <AttachmentList attachments={attachments} />;
}

function ReportButton(props: { disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      title="Report this to moderators"
      aria-label="Report this to moderators"
      disabled={props.disabled}
      onClick={props.onClick}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-zinc-400 transition hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
    >
      <CircleAlert className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

function HeaderIconButton(props: {
  title: string;
  disabled?: boolean;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={props.title}
      aria-label={props.title}
      disabled={props.disabled}
      onClick={props.onClick}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-full border text-zinc-800 transition disabled:opacity-50 ${
        props.active
          ? 'border-zinc-400 bg-zinc-50 text-zinc-900 ring-1 ring-zinc-200'
          : 'border-zinc-300 bg-white hover:bg-zinc-50'
      }`}
    >
      {props.children}
    </button>
  );
}

function TopicNotificationPill(props: {
  value: 'off' | 'immediate';
  disabled?: boolean;
  onChange: (enabled: boolean) => void;
}) {
  const enabled = props.value === 'immediate';
  const optionClass = (active: boolean) =>
    `rounded-full px-2 py-1 text-xs font-medium transition sm:px-3 ${
      active ? 'bg-accent text-white' : 'text-zinc-700 hover:bg-zinc-100'
    }`;

  return (
    <div className="inline-flex h-9 max-w-full items-center gap-0.5 whitespace-nowrap rounded-full border border-zinc-300 bg-white p-1 sm:gap-1">
      <span
        className={`inline-flex h-7 w-6 items-center justify-center rounded-full sm:w-7 ${
          enabled ? 'text-red-600' : 'text-zinc-500'
        }`}
        title="Topic reply notifications"
        aria-hidden
      >
        <Bell className="h-4 w-4" />
      </span>
      <button
        type="button"
        className={optionClass(!enabled)}
        disabled={props.disabled}
        aria-pressed={!enabled}
        onClick={() => props.onChange(false)}
      >
        Off
      </button>
      <button
        type="button"
        className={optionClass(enabled)}
        disabled={props.disabled}
        aria-pressed={enabled}
        onClick={() => props.onChange(true)}
      >
        New replies
      </button>
    </div>
  );
}

function TopicCategoryRows(props: {
  rows: Tag[][];
  selectedIds: string[];
  onToggle: (tag: Tag) => void;
}) {
  return (
    <div className="space-y-2">
      {props.rows.map((row, index) => (
        <div key={index} className="flex flex-wrap gap-2">
          {row.map((tag) => (
            <TagChip
              key={tag.id}
              label={tag.name}
              selected={props.selectedIds.includes(tag.id)}
              onClick={() => props.onToggle(tag)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function TopicPage() {
  const { topicId } = useParams();
  const { user } = useAuth();
  const memberDirectory = useMemberDirectory();
  const [topic, setTopic] = React.useState<DiscussionTopic | null>(null);
  const [topicLoaded, setTopicLoaded] = React.useState(false);
  const [tags, setTags] = React.useState<Tag[]>([]);
  const [draftTagIds, setDraftTagIds] = React.useState<string[]>([]);
  const [editingTopic, setEditingTopic] = React.useState(false);
  const [editTitle, setEditTitle] = React.useState('');
  const [editBody, setEditBody] = React.useState('');
  const [updatingTopic, setUpdatingTopic] = React.useState(false);
  const [topicAttachments, setTopicAttachments] = React.useState<DiscussionAttachment[]>([]);
  const [replyAttachmentFiles, setReplyAttachmentFiles] = React.useState<File[]>([]);
  const [deletingAttachmentId, setDeletingAttachmentId] = React.useState('');
  const [replies, setReplies] = React.useState<DiscussionReply[]>([]);
  const [body, setBody] = React.useState('');
  const [replyTo, setReplyTo] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [reportingTargetId, setReportingTargetId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [notificationSettings, setNotificationSettings] = React.useState(DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS);
  const trackedTopicViewRef = React.useRef<string>('');
  const replyPanelRef = React.useRef<HTMLDivElement | null>(null);
  const { askReplyEmailNotification, replyEmailNotificationPrompt } = useReplyEmailNotificationPrompt();

  React.useEffect(() => {
    if (!topicId) return;
    setTopicLoaded(false);
    getTopic(topicId)
      .then(setTopic)
      .catch(() => setTopic(null))
      .finally(() => setTopicLoaded(true));
    const unsub = subscribeReplies(topicId, setReplies);
    return () => unsub();
  }, [topicId]);

  React.useEffect(() => {
    if (!topicId) return;
    return subscribeTopicAttachments(topicId, setTopicAttachments);
  }, [topicId]);

  React.useEffect(() => {
    listTags('discussion', true).then(setTags).catch(() => setTags([]));
  }, []);

  React.useEffect(() => {
    if (!user?.sub) return;
    return subscribeDiscussionNotificationSettings(user.sub, setNotificationSettings);
  }, [user?.sub]);

  React.useEffect(() => {
    setDraftTagIds((topic?.tagIds ?? []).map(normalizeDiscussionCategoryId));
  }, [topic?.id, topic?.tagIds]);

  React.useEffect(() => {
    if (!topic || editingTopic) return;
    setEditTitle(topic.title || '');
    setEditBody(topic.body || '');
  }, [editingTopic, topic]);

  React.useEffect(() => {
    if (!topic?.id) return;
    if (trackedTopicViewRef.current === topic.id) return;
    trackedTopicViewRef.current = topic.id;
    void trackEvent('view_topic', {
      topic_id: topic.id,
      tag_count: topic.tagIds?.length ?? 0,
      reply_count: replies.length
    });
  }, [topic?.id, topic?.tagIds, replies.length]);

  const tagById = React.useMemo(() => {
    const map = new Map<string, Tag>();
    for (const tag of tags) map.set(tag.id, tag);
    return map;
  }, [tags]);

  const topicTags = React.useMemo(() => {
    if (!topic?.tagIds?.length) return [];
    return resolveDiscussionTopicTags(topic.tagIds, tags);
  }, [topic?.tagIds, tags]);

  const categoryRows = React.useMemo(() => groupDiscussionTags(tags), [tags]);

  const topicOverride = React.useMemo(() => {
    if (!topic?.id) return null;
    return topicOverrideMode(notificationSettings, topic.id);
  }, [notificationSettings, topic?.id]);
  const topicReplyEmailMode = topicOverride === 'immediate' ? 'immediate' : 'off';

  const isTopicAuthor = Boolean(topic && user?.sub && topic.authorProfileId === user.sub);

  async function saveTopicReplyEmailPreference(enabled: boolean) {
    if (!topic?.id || !user?.sub) return;
    await setTopicReplyEmailNotifications(user.sub, topic.id, enabled);
  }

  function cancelTopicEdit() {
    if (!topic) {
      setEditingTopic(false);
      return;
    }
    setEditingTopic(false);
    setEditTitle(topic.title || '');
    setEditBody(topic.body || '');
    setDraftTagIds((topic.tagIds ?? []).map(normalizeDiscussionCategoryId));
  }

  function toggleTopicEdit() {
    if (!topic) return;
    if (editingTopic) {
      cancelTopicEdit();
      return;
    }
    setEditTitle(topic.title || '');
    setEditBody(topic.body || '');
    setDraftTagIds((topic.tagIds ?? []).map(normalizeDiscussionCategoryId));
    setEditingTopic(true);
    setError(null);
  }

  async function saveTopicEdit() {
    if (!topicId || !topic || !user?.sub || updatingTopic) return;
    const nextTitle = isTopicAuthor ? editTitle.trim() : String(topic.title || '').trim();
    const nextBody = isTopicAuthor ? editBody.trim() : String(topic.body || '').trim();
    if (isTopicAuthor && (!nextTitle || !nextBody)) return;

    const nextTagIds = Array.from(new Set(draftTagIds));
    const currentSet = new Set(topic.tagIds || []);
    const nextSet = new Set(nextTagIds);
    const added = nextTagIds.filter((id) => !currentSet.has(id));
    const removed = (topic.tagIds || []).filter((id) => !nextSet.has(id));
    const titleChanged = isTopicAuthor && nextTitle !== String(topic.title || '').trim();
    const bodyChanged = isTopicAuthor && nextBody !== String(topic.body || '').trim();
    const categoriesChanged = added.length > 0 || removed.length > 0;

    if (!titleChanged && !bodyChanged && !categoriesChanged) {
      setEditingTopic(false);
      return;
    }

    setUpdatingTopic(true);
    setError(null);
    try {
      if (titleChanged || bodyChanged) {
        await editDiscussionTopic({
          topicId,
          title: nextTitle,
          body: nextBody,
          authorDisplayName: formatPersonName(user.displayName)
        });
        await trackEvent('edit_topic', {
          topic_id: topicId,
          title_changed: titleChanged,
          body_changed: bodyChanged,
          categories_changed: categoriesChanged
        });
      }

      if (categoriesChanged) {
        await updateTopicTagsWithSystemReply({
          topicId,
          authorProfileId: user.sub,
          authorDisplayName: formatPersonName(user.displayName),
          tagIds: nextTagIds,
          addedTagNames: added.map((id) => tagById.get(id)?.name || id),
          removedTagNames: removed.map((id) => tagById.get(id)?.name || id)
        });
      }

      const ts = Date.now();
      setTopic((prev) =>
        prev
          ? {
              ...prev,
              title: nextTitle,
              body: nextBody,
              tagIds: nextTagIds,
              ...(categoriesChanged ? { lastActivityAt: ts } : {}),
              updatedAt: ts
            }
          : prev
      );
      setEditingTopic(false);
    } catch (e: any) {
      console.error(e);
      setError(e?.message ? String(e.message) : 'Failed to update topic');
    } finally {
      setUpdatingTopic(false);
    }
  }

  async function deleteTopicAttachment(attachment: DiscussionAttachment) {
    if (!isTopicAuthor || deletingAttachmentId) return;
    setDeletingAttachmentId(attachment.id);
    setError(null);
    try {
      await deleteDiscussionAttachment(attachment);
      await trackEvent('delete_topic_attachment', {
        topic_id: attachment.topicId,
        content_type: attachment.contentType,
        size_bytes: attachment.sizeBytes
      });
    } catch (e: any) {
      console.error(e);
      setError(e?.message ? String(e.message) : 'Failed to delete attachment');
    } finally {
      setDeletingAttachmentId('');
    }
  }

  async function reportTopic() {
    if (!topicId || !topic || reportingTargetId) return;
    const confirmed = window.confirm('Report this post to moderators? It will be hidden until reviewed.');
    if (!confirmed) return;

    setReportingTargetId(`topic:${topicId}`);
    setError(null);
    try {
      await reportDiscussionContent({ targetType: 'topic', topicId });
      setTopic(null);
      setReplies([]);
      setTopicAttachments([]);
      setTopicLoaded(true);
    } catch (e: any) {
      console.error(e);
      setError(e?.message ? String(e.message) : 'Failed to report this post');
    } finally {
      setReportingTargetId('');
    }
  }

  async function reportReply(replyId: string) {
    if (!topicId || !replyId || reportingTargetId) return;
    const confirmed = window.confirm('Report this reply to moderators? It will be hidden until reviewed.');
    if (!confirmed) return;

    setReportingTargetId(`reply:${replyId}`);
    setError(null);
    try {
      await reportDiscussionContent({ targetType: 'reply', topicId, replyId });
      setReplies((current) => current.filter((reply) => reply.id !== replyId));
    } catch (e: any) {
      console.error(e);
      setError(e?.message ? String(e.message) : 'Failed to report this reply');
    } finally {
      setReportingTargetId('');
    }
  }

  async function submit() {
    if (!topicId) return;
    if (!body.trim() || !user?.sub) return;
    if (submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const trimmedBody = body.trim();
      const payload: any = {
        topicId,
        body: trimmedBody,
        authorProfileId: user.sub,
        authorDisplayName: formatPersonName(user.displayName)
      };

      // Only include parentReplyId when replying to a specific message.
      // Firestore rejects `undefined` field values.
      if (replyTo) payload.parentReplyId = replyTo;

      const replyId = await addReply(payload);
      if (replyAttachmentFiles.length) {
        await uploadDiscussionAttachments({
          target: { kind: 'reply', topicId, replyId },
          ownerProfileId: user.sub,
          files: replyAttachmentFiles
        });
      }
      await trackEvent('post_reply', {
        topic_id: topicId,
        reply_target: replyTo ? 'reply' : 'topic',
        attachment_count: replyAttachmentFiles.length,
        body_length: trimmedBody.length
      });

      const wantsReplyEmails = await askReplyEmailNotification();
      await setTopicReplyEmailNotifications(user.sub, topicId, wantsReplyEmails).catch((notificationError) => {
        console.error(notificationError);
        setError('Reply posted, but notification preference could not be saved.');
      });

      setBody('');
      setReplyAttachmentFiles([]);
      setReplyTo(null);
    } catch (e: any) {
      console.error(e);
      setError(e?.message ? String(e.message) : 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  }

  function scrollToReplyPanel() {
    window.setTimeout(() => {
      const panel = replyPanelRef.current;
      if (!panel) return;

      const header = document.querySelector('header');
      const headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
      const top = panel.getBoundingClientRect().top + window.scrollY - headerHeight - 12;
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'smooth' });
    }, 0);
  }

  function startReplyTo(replyId: string) {
    setReplyTo(replyId);
    scrollToReplyPanel();
  }

  function addReplyAttachmentFiles(fileList: FileList | null) {
    if (!fileList) return;
    const next = [...replyAttachmentFiles, ...Array.from(fileList)];
    const validationError = validateAttachmentFiles(next);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setReplyAttachmentFiles(next);
  }

  function removeReplyAttachmentFile(index: number) {
    const next = replyAttachmentFiles.filter((_, fileIndex) => fileIndex !== index);
    setError(validateAttachmentFiles(next) || null);
    setReplyAttachmentFiles(next);
  }


  const { top, children } = groupThread(replies);
  const topicAuthor = topic ? memberLabel(topic.authorProfileId, topic.authorDisplayName, memberDirectory) : '';

  if (!topicId) {
    return <div className="text-sm text-zinc-600">Missing topic id</div>;
  }

  if (topicLoaded && !topic) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <Card>
          <CardBody className="space-y-3">
            <div className="text-sm text-zinc-700">This discussion is no longer available.</div>
            <Link to="/discussions">
              <Button variant="secondary">Back to discussions</Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <>
      {replyEmailNotificationPrompt}
      <div className="grid grid-cols-1 gap-4">
      <Card>
        <div className="flex flex-col items-stretch gap-3 border-b border-zinc-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 md:flex-1">
            <div className="min-w-0">
              <h1 className="break-words text-xl font-semibold leading-tight text-zinc-900">
                {topic ? topic.title : 'Topic'}
              </h1>
              {topicTags.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {topicTags.map((tag) => (
                    <span
                      key={tag.id}
                      className="rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700"
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center justify-start gap-2 md:w-auto md:shrink-0 md:justify-end">
            {topic ? (
              <HeaderIconButton title="Edit" active={editingTopic} onClick={toggleTopicEdit} disabled={!topic}>
                <Pencil className="h-4 w-4" aria-hidden />
              </HeaderIconButton>
            ) : null}
            {topic && user?.sub ? (
              <TopicNotificationPill
                value={topicReplyEmailMode}
                onChange={(enabled) => void saveTopicReplyEmailPreference(enabled)}
              />
            ) : null}
            <Link to="/discussions">
              <Button variant="secondary">Back</Button>
            </Link>
          </div>
        </div>
        <CardBody className="space-y-4">
          {topic ? (
            <div className="rounded-2xl border border-zinc-200 bg-white p-3">
              {editingTopic ? (
                <div className="space-y-2">
                  {isTopicAuthor ? (
                    <>
                      <Input value={editTitle} onChange={(event) => setEditTitle(event.target.value)} placeholder="Title" />
                      <Textarea
                        value={editBody}
                        onChange={(event) => setEditBody(event.target.value)}
                        placeholder="Original post"
                        rows={6}
                      />
                    </>
                  ) : null}
                  <div className="space-y-2 rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                    <div className="text-xs font-medium text-zinc-700">Categories</div>
                    <TopicCategoryRows
                      rows={categoryRows}
                      selectedIds={draftTagIds}
                      onToggle={(tag) =>
                        setDraftTagIds((prev) =>
                          prev.includes(tag.id) ? prev.filter((id) => id !== tag.id) : [...prev, tag.id]
                        )
                      }
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" onClick={cancelTopicEdit} disabled={updatingTopic}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => void saveTopicEdit()}
                      disabled={(isTopicAuthor && (!editTitle.trim() || !editBody.trim())) || updatingTopic}
                    >
                      {updatingTopic ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="whitespace-pre-wrap text-sm text-zinc-800">
                    <LinkifiedText text={topic.body} />
                  </div>
                  <AttachmentList attachments={topicAttachments} />
                </>
              )}

              {editingTopic ? (
                <AttachmentList
                  attachments={topicAttachments}
                  canDelete={isTopicAuthor}
                  deletingAttachmentId={deletingAttachmentId}
                  onDelete={(attachment) => void deleteTopicAttachment(attachment)}
                />
              ) : null}

              <div className="mt-2 flex items-center justify-between gap-2 text-xs text-zinc-600">
                <a
                  href={memberPublicProfileUrl(topic.authorProfileId)}
                  className="truncate text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
                  title={topicAuthor}
                >
                  {topicAuthor}
                </a>
                <div className="flex shrink-0 items-center gap-1.5">
                  <DiscussionTopicLikeControl
                    topicId={topicId}
                    profileId={user?.sub}
                    likeCount={topic.likeCount}
                    profilesById={memberDirectory.profilesById}
                  />
                  <ReportButton
                    disabled={reportingTargetId === `topic:${topicId}`}
                    onClick={() => void reportTopic()}
                  />
                  <span title={formatExactDateTime(topic.createdAt || topic.updatedAt)}>
                    Created {formatRelativeDateTime(topic.createdAt || topic.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-600">Loading</div>
          )}

          {topic?.hasPoll ? <DiscussionPoll topicId={topic.id} profileId={user?.sub} /> : null}

          <div className="space-y-3">

            {top.map((r) => (
              (() => {
                const author = memberLabel(r.authorProfileId, r.authorDisplayName, memberDirectory);
                return (
              <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 whitespace-pre-wrap text-sm text-zinc-800">
                    <LinkifiedText text={r.body} />
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {r.systemAction ? null : (
                      <Button variant="ghost" onClick={() => startReplyTo(r.id)}>
                        Reply
                      </Button>
                    )}
                  </div>
                </div>
                <ReplyAttachments topicId={topicId} replyId={r.id} />
                <div className="mt-2 flex items-center justify-between">
                  <a
                    href={memberPublicProfileUrl(r.authorProfileId)}
                    className="min-w-0 truncate text-xs text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
                    title={author}
                  >
                    {author}
                  </a>
                  <div className="flex shrink-0 items-center gap-1.5 text-xs text-zinc-600">
                    {r.systemAction ? null : (
                      <DiscussionReplyLikeControl
                        topicId={topicId}
                        replyId={r.id}
                        profileId={user?.sub}
                        likeCount={r.likeCount}
                      />
                    )}
                    <ReportButton
                      disabled={reportingTargetId === `reply:${r.id}`}
                      onClick={() => void reportReply(r.id)}
                    />
                    <span title={formatExactDateTime(r.createdAt || r.updatedAt)}>
                      Replied {formatRelativeDateTime(r.createdAt || r.updatedAt)}
                    </span>
                  </div>
                </div>

                {children[r.id]?.length ? (
                  <div className="mt-3 space-y-2 border-l border-zinc-200 pl-3">
                    {children[r.id].map((c) => (
                      (() => {
                        const childAuthor = memberLabel(c.authorProfileId, c.authorDisplayName, memberDirectory);
                        return (
                      <div key={c.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                        <div className="whitespace-pre-wrap text-sm text-zinc-800">
                          <LinkifiedText text={c.body} />
                        </div>
                        <ReplyAttachments topicId={topicId} replyId={c.id} />

                        {/* Second level: no Reply button (prevents confusing “third level”) */}
                        <div className="mt-2 flex items-center justify-between">
                          <a
                            href={memberPublicProfileUrl(c.authorProfileId)}
                            className="min-w-0 truncate text-xs text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
                            title={childAuthor}
                          >
                            {childAuthor}
                          </a>
                          <span
                            className="text-xs text-zinc-600"
                            title={formatExactDateTime(c.createdAt || c.updatedAt)}
                          >
                          <span className="inline-flex items-center gap-1.5">
                              {c.systemAction ? null : (
                                <DiscussionReplyLikeControl
                                  topicId={topicId}
                                  replyId={c.id}
                                  profileId={user?.sub}
                                  likeCount={c.likeCount}
                                />
                              )}
                              <ReportButton
                                disabled={reportingTargetId === `reply:${c.id}`}
                                onClick={() => void reportReply(c.id)}
                              />
                              <span>Replied {formatRelativeDateTime(c.createdAt || c.updatedAt)}</span>
                            </span>
                          </span>
                        </div>
                      </div>
                        );
                      })()
                    ))}
                  </div>
                ) : null}
              </div>
                );
              })()
            ))}
          </div>

          <div
            ref={replyPanelRef}
            className="grid grid-cols-1 gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 p-3 md:grid-cols-[minmax(0,1fr)_minmax(9rem,16rem)]"
          >
            <Textarea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a reply"
              className="min-h-32"
            />
            <div className="flex min-w-0 flex-col gap-2">
              <Button onClick={submit} disabled={!body.trim() || submitting} className="w-full">
                {submitting ? 'Posting…' : 'Post reply'}
              </Button>
              <AttachmentPickerLayout
                files={replyAttachmentFiles}
                disabled={submitting}
                onAddFiles={addReplyAttachmentFiles}
                onRemoveFile={removeReplyAttachmentFile}
                className="flex min-w-0 flex-col items-stretch gap-2"
                chipClassName="w-full max-w-full justify-between"
                buttonClassName="w-full"
                errorClassName="text-xs text-red-700"
              />
              {replyTo ? (
                <Button variant="ghost" onClick={() => setReplyTo(null)} className="w-full">
                  Cancel reply-to
                </Button>
              ) : null}
            </div>
            {error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-800 md:col-span-2">
                {error}
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>
      </div>
    </>
  );
}
